import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { ChanneltalkInfo } from '../types';
import { ChanneltalkConnectModal } from './ChanneltalkConnectModal';
import ChannelTalkImportSettingModal from './ChannelTalkImportSettingModal';
import ChanneltalkTriggerSettingModal from './ChanneltalkTriggerSettingModal';
import { useChanneltalk } from './useChanneltalk';

export const ChannelTalkItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const { workspaces, disconnectMutation, requestImportMutation } =
    useChanneltalk(!isConnector);

  const connectModalState = useModalState();

  const settingModalState = useModalState<{
    channel: ChanneltalkInfo;
  }>();
  const triggerModalState = useModalState<{
    channel: ChanneltalkInfo;
  }>();

  const importModalState = useModalState<{
    workspace: ChanneltalkInfo;
    isNew: boolean;
  }>();

  const handleConnectComplete = (workspace: ChanneltalkInfo) => {
    if (!currWorkspace) return;
    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
    importModalState.open({
      modalState: {
        workspace,
        isNew: true,
      },
    });
  };

  const handleDisconnect = (info: ChanneltalkInfo) => {
    disconnectMutation(info.alias);
  };

  const handleImportWorkspace = async (
    channelAlias: string,
    dateRange: NullableDateRangeT,
  ) => {
    requestImportMutation({
      channelAlias,
      dateRange,
    });
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={true}
        connected={!!workspaces}
        source={SOURCE_TYPE.CHANNELTALK}
        description="Import conversations as feedbacks into Syncly."
        connectedDisplay={() => 'ChannelTalk workspace'}
        infos={workspaces || []}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        onAddInfoClick={() => connectModalState.open()}
        additionalActions={(workspace: ChanneltalkInfo) => (
          <div className="flex gap-2">
            <Button
              variant="outlined"
              onClick={() =>
                triggerModalState.open({ modalState: { channel: workspace } })
              }
            >
              Trigger option
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                settingModalState.open({ modalState: { channel: workspace } })
              }
            >
              Import option
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace: workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <ChanneltalkConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={settingModalState.status}
        closeModal={settingModalState.close}
        useBackgroundClose
      >
        <ChannelTalkImportSettingModal
          channel={settingModalState.modalState?.channel}
        />
      </Modal>
      <Modal
        status={triggerModalState.status}
        closeModal={triggerModalState.close}
        useBackgroundClose
      >
        <ChanneltalkTriggerSettingModal
          alias={triggerModalState.modalState?.channel.alias}
          importRule={triggerModalState.modalState?.channel.import_rule}
        />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import feedback'}
          subTitle={'Atlas'}
          subTitleList={importModalState.modalState?.workspace?.alias}
          historyList={importModalState.modalState?.workspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImportWorkspace(
              importModalState.modalState?.workspace?.alias || '',
              {
                start_date,
                end_date,
              },
            )
          }
        />
      </Modal>
    </>
  );
};
